import { useCallback } from 'react';
import { get } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { alertSrc } from '#shared/assets';
import { useConfirm } from '#shared/lib/hooks';
import { constructorTestIds } from '#shared/lib/testids';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import {
    changeConstructorSection,
    detachConstructorSection,
    getConstructorPagesSettings,
    getConstructorSectionSettings,
    useConstructorRightBarSection,
} from '../model/slices';
import { useChangeConstructorRightBarSection } from './useChangeConstructorRightBarSection';

export const useConstructorReplaceSection = () => {
    const { confirm, renderConfirmModal } = useConfirm();
    const currentSection = useConstructorRightBarSection();
    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();

    // Если id старой секции и новой одинаковые
    // Что меняем | На что меняем | Что получаем
    //   parent   |   parent      | Ничего не происходит
    //   parent   |   child       | Ничего не происходит
    //   parent   |   usual       | Изначальное состояние невалидное, превращаем все секции в usual
    //   child    |   parent      | Ничего не происходит
    //   child    |   child       | Ничего не происходит
    //   child    |   usual       | Изначальное состояние невалидное, превращаем все секции в usual
    //   usual    |   parent      | Изначальное состояние невалидное, превращаем все секции в usual
    //   usual    |   child       | Изначальное состояние невалидное, превращаем все секции в usual
    //   usual    |   usual       | Ничего не происходит

    // Если id старой секции и новой разные
    // Что меняем | На что меняем | Что получаем
    //   parent   |   parent      |  parent и child старой секции превращаются в child новой секции
    //   parent   |   child       |  parent и child старой секции превращаются в child новой секции
    //   parent   |   usual       |  parent и child старой секции превращаются в parent и child новой секции
    //   child    |   parent      |  child старой секции превращается в child новой секции
    //   child    |   child       |  child старой секции превращается в child новой секции
    //   child    |   usual       |  child старой секции превращается в usual новой секции
    //   usual    |   parent      |  usual старой секции превращается в child новой секции
    //   usual    |   child       |  usual старой секции превращается в child новой секции
    //   usual    |   usual       |  usual старой секции превращается в usual новой секции

    const replaceSection = useCallback(
        async ({
            pageId,
            sectionInstanceId,
            newSectionSettings,
        }: {
            pageId: string;
            sectionInstanceId: string;
            newSectionSettings: Object;
        }) => {
            if (!newSectionSettings) {
                return;
            }

            const final = () => {
                if (currentSection?.sectionInstanceId === sectionInstanceId) {
                    changeConstructorRightBarSection();
                } else {
                    changeConstructorRightBarSection(sectionInstanceId);
                }
            };

            const oldSection = getConstructorSectionSettings(pageId, sectionInstanceId);
            const newId = get(newSectionSettings, ['id']);
            const newSectionType = get(newSectionSettings, ['section_type']);
            const newSectionSettingsInstanceId = get<string>(newSectionSettings, ['instance_id']) ?? '';

            // См. первую таблицу выше
            if (oldSection.id === newId) {
                if (
                    oldSection.section_type === newSectionType ||
                    (oldSection.section_type === 'parent' && newSectionType === 'child') ||
                    (oldSection.section_type === 'child' && newSectionType === 'parent')
                ) {
                    // Ничего не делаем
                    return;
                }

                detachConstructorSection({ pageId, sectionInstanceId, detachAll: true });
                detachConstructorSection({ pageId, sectionInstanceId: newSectionSettingsInstanceId, detachAll: true });
                logEventString(constructorProductLoggingEvents.SectionType.Replace, 'true', {
                    pageId,
                    sectionInstanceId,
                    oldSectionType: oldSection.section_type,
                    newSectionType: String(newSectionType),
                    mode: 'sameSectionId',
                });
                final();

                return;
            }

            if (oldSection.section_type === 'parent') {
                const isConfirmed = await confirm({
                    imageSrc: alertSrc,
                    title: 'Уверены, что хотите заменить родительский компонент?',
                    bodyText: 'Все дочерние элементы унаследуют изменения.',
                    applyText: 'Да, заменить',
                    'data-testid': constructorTestIds.sectionReplaceConfirmModal,
                    applyButtonTestId: constructorTestIds.sectionReplaceConfirm,
                    cancelButtonTestId: constructorTestIds.sectionReplaceCancel,
                });

                if (!isConfirmed) {
                    return;
                }

                const pages = getConstructorPagesSettings();

                logEventString(constructorProductLoggingEvents.SectionType.Replace, 'true', {
                    pageId,
                    sectionInstanceId,
                    oldSectionType: oldSection.section_type,
                    newSectionType: String(newSectionType),
                    mode: 'replaceParentAcrossPages',
                });

                Object.values(pages).forEach((page) => {
                    page.layout.forEach((section) => {
                        if (section.id === oldSection.id) {
                            changeConstructorSection(page.id, section.instance_id, {
                                ...newSectionSettings,
                                display_sequence: section.display_sequence,
                                section_type:
                                    !newSectionType || newSectionType === 'usual' ? section.section_type : 'child',
                                instance_id: self.crypto.randomUUID(),
                                id: !newSectionType || newSectionType === 'usual' ? section.id : newId,
                            });
                        }
                    });
                });
                final();

                return;
            }

            logEventString(constructorProductLoggingEvents.SectionType.Replace, 'true', {
                pageId,
                sectionInstanceId,
                oldSectionType: oldSection.section_type,
                newSectionType: String(newSectionType),
                mode: 'singleSection',
            });

            changeConstructorSection(pageId, sectionInstanceId, {
                ...newSectionSettings,
                display_sequence: oldSection.display_sequence,
                section_type: !newSectionType || newSectionType === 'usual' ? 'usual' : 'child',
                instance_id: self.crypto.randomUUID(),
                id: !newSectionType || newSectionType === 'usual' ? self.crypto.randomUUID() : newId,
            });

            final();
        },
        [changeConstructorRightBarSection, currentSection, confirm]
    );

    return {
        replaceSection,
        renderReplaceConfirmModal: renderConfirmModal,
    };
};
